import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { CustomModalLayout, Modal } from 'wix-style-react';
import { API_INSTANCE } from 'src/features/utils';
import { selectSiteData } from 'src/features/account-slice';
import { Copy } from 'src/modules/events/components/copy';
import { useAppSelector, useAppDispatch } from 'src/hooks';
import { accountSelector } from 'src/redesign/features/Account/store/selectors';
import { TabView, TabPanel } from 'primereact/tabview';
import { RedesignButton } from 'src/redesign/components/RedesignButton';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputText } from 'primereact/inputtext';
import { InputOtp } from 'primereact/inputotp';
import { sendPinCode, verifyPinCode } from 'src/redesign/pages/Login/store/actions';
import { codeIdSelector } from 'src/redesign/pages/Login/store/selectors';
import { setLoginEmailAction } from 'src/redesign/pages/Login/store/reducer';
import styles from './AccountSettingsModal.module.scss';
import { showNotification } from '../../Toast/store/actions';

type AccountFormData = {
  first_name: string;
  last_name: string;
  handle: string;
  display_name: string;
  personal_website_url: string;
  linkedin_profile_url: string;
  youtube_profile_url: string;
  useCustomSiteHandle: boolean;
};

type AccountSettingsModalProps = {
  isVisible?: boolean;
  setVisible: (value: boolean) => void;
};

// Password Change Section Component
const PasswordChangeSection: FC<{ email: string }> = ({ email }) => {
  const [newPassword, setNewPassword] = useState('');
  const [newConfirmPassword, setNewConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pinCode, setPinCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(20);
  const cooldownTimerRef = useRef(null);
  const pinOverlay = useRef(null);
  const dispatch = useAppDispatch();
  const site = useSelector(selectSiteData);
  const codeID = useAppSelector(codeIdSelector);

  const sendCode = useCallback(() => {
    dispatch(setLoginEmailAction(email));
    dispatch(sendPinCode({ site, email }));
    setCooldownActive(true);
    setCooldownTime(20);
    setError(null); // Clear any errors when resending

    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }

    cooldownTimerRef.current = setInterval(() => {
      setCooldownTime((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current);
          setCooldownActive(false);
          return 20;
        }
        return prev - 1;
      });
    }, 1000);
  }, [dispatch, email, site]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  const handleChangePasswordClick = (e) => {
    // Validate passwords
    if (!newPassword || !newConfirmPassword) {
      setError('Please enter both password fields');
      return;
    }
    if (newPassword !== newConfirmPassword) {
      setError('The new passwords do not match. Please try again.');
      return;
    }
    if (newPassword.length <= 8) {
      setError('The password must be longer than 8 characters.');
      return;
    }

    setError(null);
    setPinCode(''); // Clear any previous PIN

    // Show PIN overlay and send PIN code
    pinOverlay.current.toggle(e);
    sendCode();
  };

  const verifyCodeAndChangePassword = useCallback(async () => {
    if (pinCode.length !== 6) {
      return;
    }

    setVerifying(true);

    try {
      // Step 1: Verify PIN code to get verificationCode
      const { data } = await API_INSTANCE.post(
        `/api/v1/dashboard/accounts/change-password-code-verification`,
        {
          email,
          code_verification: { code: pinCode, id: codeID },
        },
      );

      const verificationCode = data.verificationCode;

      // Step 2: Call change-password with the verificationCode
      await API_INSTANCE.post(`/api/v1/dashboard/accounts/change-password`, {
        new_password: newPassword,
        new_password_confirm: newConfirmPassword,
        verificationCode: verificationCode,
      });

      // Success - clear form and close overlay
      setNewPassword('');
      setNewConfirmPassword('');
      setPinCode('');
      setError(null);
      pinOverlay.current.hide();

      dispatch(
        showNotification({
          severity: 'success',
          summary: 'Success',
          detail: 'Your password has been changed successfully.',
          life: 3000,
        }),
      );
    } catch (err) {
      console.error(err);
      setPinCode(''); // Clear PIN on error to allow retry
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to verify code. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  }, [email, codeID, pinCode, newPassword, newConfirmPassword, dispatch]);

  return (
    <div className={styles.passwordSection}>
      <div className={styles.sectionTitle}>Change Password</div>

      <div className={styles.passwordGrid}>
        <div className={styles.formGroup}>
          <label className={styles.label}>New Password</label>
          <InputText
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Confirm New Password</label>
          <InputText
            type="password"
            value={newConfirmPassword}
            onChange={(e) => setNewConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            className={styles.input}
          />
        </div>
      </div>

      {error && <div className={styles.errorText}>{error}</div>}

      <RedesignButton
        label="Change Password"
        onClick={handleChangePasswordClick}
        variation="primary"
        disabled={!newPassword || !newConfirmPassword}
      />

      <OverlayPanel ref={pinOverlay} className={styles.pinOverlay}>
        <div className={styles.pinVerificationContent}>
          <h3 className={styles.pinTitle}>Verify Your Email</h3>
          <p className={styles.pinSubtitle}>
            Enter the 6-digit code sent to <strong>{email}</strong>
          </p>

          <InputOtp
            value={pinCode}
            onChange={(e) => setPinCode(e.value)}
            length={6}
            className={styles.inputOtp}
            disabled={verifying}
          />

          {verifying && <p className={styles.verifyingMessage}>Verifying...</p>}
          {error && <div className={styles.pinErrorText}>{error}</div>}

          <div className={styles.pinButtonWrapper}>
            {cooldownActive ? (
              <RedesignButton
                text
                label={`Resend in ${cooldownTime}s`}
                disabled
                className={styles.resendButton}
              />
            ) : (
              <RedesignButton
                text
                label="Resend Code"
                onClick={sendCode}
                className={styles.resendButton}
              />
            )}
          </div>

          <RedesignButton
            label="Submit Code"
            onClick={verifyCodeAndChangePassword}
            disabled={verifying || pinCode.length !== 6}
            variation="primary"
            className={styles.submitButton}
          />
        </div>
      </OverlayPanel>
    </div>
  );
};

export const AccountSettingsModal: FC<AccountSettingsModalProps> = ({ isVisible, setVisible }) => {
  const [loading, setLoading] = useState(false);
  const site = useSelector(selectSiteData);
  const account = useAppSelector(accountSelector);
  const dispatch = useAppDispatch();

  const { handleSubmit, control } = useForm<AccountFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      handle: '',
      personal_website_url: '',
      display_name: '',
      linkedin_profile_url: '',
      youtube_profile_url: '',
      ...account,
    },
  });

  const closeModal = () => {
    setVisible(false);
  };

  const onSubmit: SubmitHandler<AccountFormData> = async (data) => {
    setLoading(true);

    try {
      const newData = data;
      await API_INSTANCE.put(`/api/v1/dashboard/accounts`, newData);

      dispatch(
        showNotification({
          severity: 'success',
          summary: 'Update Account',
          detail: 'Successfully Saved Changes',
          life: 3000,
        }),
      );
    } catch (error) {
      console.error(error);
      dispatch(
        showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save changes',
          life: 3000,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => (
    <div className={styles.tabContainer}>
      <TabView className={styles.tabView}>
        {/* Profile Tab */}
        <TabPanel header="Profile">
          <div className={styles.tabContent}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <ProfileForm control={control} />
              <div className={styles.buttonGroup}>
                <RedesignButton
                  label="Save Changes"
                  type="submit"
                  variation="primary"
                  loading={loading}
                />
              </div>
            </form>
          </div>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel header="Security">
          <div className={styles.tabContent}>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Email Address</div>
              <div className={styles.emailDisplay}>{account?.email}</div>
              <div className={styles.helperText}>Your email address cannot be changed</div>
            </div>

            <div className={styles.divider} />

            <PasswordChangeSection email={account?.email} />
          </div>
        </TabPanel>

        {/* Referral Tab */}
        <TabPanel header="Referral Program">
          <div className={styles.tabContent}>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Referral Link</div>
              <div className={styles.sectionDescription}>
                Earn a 20% recurring commission for each subscription initiated through your unique
                referral link.
              </div>
              <Copy
                text={`https://ticketspotapp.com/ref/${account?.referral_name || 'your-ref-code'}`}
              />
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>
  );

  const renderModalContent = () => (
    <CustomModalLayout
      secondaryButtonText="Close"
      onCloseButtonClick={closeModal}
      secondaryButtonOnClick={closeModal}
      title={`Account Settings`}
      width="900px"
      height="650px"
      removeContentPadding
    >
      {renderForm()}
    </CustomModalLayout>
  );

  return (
    <>
      <Modal isOpen={isVisible} onRequestClose={closeModal} screen="desktop">
        {renderModalContent()}
      </Modal>
    </>
  );
};

const ProfileForm: FC<{ control: any }> = ({ control }) => (
  <div className={styles.profileGrid}>
    <div className={styles.formGroup}>
      <label className={styles.label}>First Name</label>
      <Controller
        control={control}
        name="first_name"
        render={({ field }) => <InputText {...field} className={styles.input} />}
      />
    </div>

    <div className={styles.formGroup}>
      <label className={styles.label}>Last Name</label>
      <Controller
        control={control}
        name="last_name"
        render={({ field }) => <InputText {...field} className={styles.input} />}
      />
    </div>

    <div className={styles.formGroup}>
      <label className={styles.label}>Display Name</label>
      <Controller
        control={control}
        name="display_name"
        render={({ field }) => <InputText {...field} className={styles.input} />}
      />
    </div>

    <div className={styles.formGroup}>
      <label className={styles.label}>Personal Website</label>
      <Controller
        control={control}
        name="personal_website_url"
        render={({ field }) => (
          <InputText {...field} className={styles.input} placeholder="https://" />
        )}
      />
    </div>

    <div className={styles.formGroup}>
      <label className={styles.label}>LinkedIn Profile</label>
      <Controller
        control={control}
        name="linkedin_profile_url"
        render={({ field }) => (
          <InputText {...field} className={styles.input} placeholder="https://linkedin.com/in/" />
        )}
      />
    </div>

    <div className={styles.formGroup}>
      <label className={styles.label}>YouTube Profile</label>
      <Controller
        control={control}
        name="youtube_profile_url"
        render={({ field }) => (
          <InputText {...field} className={styles.input} placeholder="https://youtube.com/@" />
        )}
      />
    </div>
  </div>
);
